import { ColumnsType } from 'antd/lib/table'
import { Space } from 'antd'
import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { IPpcComplianceFilter } from '../../model/types/PpcComplianceFilter.interface'
import {
  selectPpcComplianceFilter,
  selectPpcCompliancePagination,
  setPpcComplianceCounter,
  setPpcCompliancePagination,
} from '../../model/ppcComplianceSlice'
import {
  GetFilterNetworkApiArg,
  NonCompliantAffiliate,
  NonCompliantAffiliatesArg,
  useExportNonCompliantAffiliates,
  useGetFilterAffiliateStatusQuery,
  useGetFilterNetworkQuery,
  useGetNonCompliantAffiliatesQuery,
  usePostAffiliateHideMutation,
  usePostAffiliateUpdateMutation,
} from '../../../../app/api'
import { IPagination } from '../../../../shared/types/Pagination.interface'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { useTranslation } from 'react-i18next'
import CountryList from '../../../../shared/ui/cells/CountriesCell'
import { AffiliateIdCell } from '../../ui/cells/AffiliateIdCell'
import { AdsCountCell } from '../../ui/cells/AdsCountCell'
import { NetworkCell } from '../../ui/cells/NetworkCell'
import { StatusCell } from '../../ui/cells/StatusCell'
import { EditCell } from '../../ui/cells/EditCell'
import { ActionsCell } from '../../ui/cells/ActionsCell'
import clsx from 'clsx'
import styles from './PpcComplianceTable.module.scss'
import { Table } from '../../../../lib/ui/table/components/table/Table'
import { DropOptions } from '../../../../shared/components/forms/drop-options/DropOptions'
import { ExportDrop } from '../../../../shared/components/cards/export-drop/ExportDrop'
import { IconArrowRight } from '../../../../shared/components/icons/IconArrowRight'
import { IconEyeOff } from '../../../../shared/components/icons/IconEyeOff'
import { IconEye } from '../../../../shared/components/icons/IconEye'
import { IconUpload } from '../../../../shared/components/icons/IconUpload'
import { AdvertiserCell } from '../../ui/cells/AdvertiserCell'
import { TabInProgressStub } from '../ppc-compliance-stubs/TabInProgressStub'
import { TabNewStub } from '../ppc-compliance-stubs/TabNewStub'
import { TabResolvedStub } from '../ppc-compliance-stubs/TabResolvedStub'
import { TabIgnoreStub } from '../ppc-compliance-stubs/TabIgnoreStub'

export const PpcComplianceTable = () => {
  const { t } = useTranslation()
  const filters = useAppSelector<IPpcComplianceFilter>(selectPpcComplianceFilter)
  const { current, pageSize, total } = useAppSelector<IPagination>(selectPpcCompliancePagination)
  const {
    project,
    country,
    affiliate,
    network,
    dateRange,
    site,
    engine,
    browser,
    city,
    hidden,
    adHijackers,
    shoppingAds,
    status,
    affiliateRule,
    keyword,
    advertiserName,
    reversed_filters,
  } = filters
  const dispatch = useAppDispatch()

  const [selectedRows, setSelectedRows] = useState<any[]>([])
  const [queryParams, setQueryParams] = useState<Partial<NonCompliantAffiliatesArg>>({
    page: current,
    'per-page': pageSize,
  })
  const { data, isLoading, isFetching, refetch } = useGetNonCompliantAffiliatesQuery(
    queryParams as NonCompliantAffiliatesArg,
    {
      skip: !queryParams.projectId,
      refetchOnMountOrArgChange: true,
    },
  )

  const { data: statuses } = useGetFilterAffiliateStatusQuery({})
  const { data: networks, refetch: networksRefetch } = useGetFilterNetworkQuery(
    { projectId: project } as unknown as GetFilterNetworkApiArg,
    { skip: !project },
  )

  const [updateStatus] = usePostAffiliateUpdateMutation()
  const [hide] = usePostAffiliateHideMutation()
  const [exportNonCompliantAffiliates, { isFetching: exportLoading }] =
    useExportNonCompliantAffiliates()

  useEffect(() => {
    setQueryParams({
      ...queryParams,
      projectId: project ? Number(project) : undefined,
      countryId: country || undefined,
      affiliateId: affiliate || undefined,
      network: network,
      date: dateRange,
      site: site,
      engineId: engine || undefined,
      browserId: browser || undefined,
      cityId: city || undefined,
      isHidden: hidden,
      isHijack: adHijackers,
      showShoppingAds: shoppingAds,
      statusId: status ? Number(status) : undefined,
      policy: adHijackers ? 'ad_hijacking' : undefined,
      affiliateRuleId: affiliateRule ? Number(affiliateRule) : undefined,
      keyword,
      advertiserId: advertiserName || undefined,
      reversedFilters: reversed_filters || undefined,
    })
  }, [filters])

  useEffect(() => {
    data && dispatch(setPpcComplianceCounter(data?.data?.tab_count))
  }, [data])

  const columns: ColumnsType<NonCompliantAffiliate> = [
    {
      title: t('table.columns.affiliateId'),
      key: 'id',
      render: (_: string, record) => <AffiliateIdCell {...record} />,
    },
    {
      title: t('table.columns.ads'),
      dataIndex: 'ads_count',
      key: 'ads_count',
      sorter: true,
      render: (_: string, record) => <AdsCountCell {...record} />,
    },
    {
      title: () => (
        <span className={clsx(styles.columnTitle)}>
          {t('table.columns.network')}
          <BpTooltip title={t('pages.PPCCompliance.networkTooltip')} />
        </span>
      ),
      key: 'network_name',
      render: (_: string, record) => (
        <NetworkCell
          {...record}
          networks={networks}
          project={project}
          onNetworksRefetch={networksRefetch}
          onRefetch={refetch}
        />
      ),
    },
    {
      title: t('table.columns.action'),
      dataIndex: 'status_id',
      key: 'status_id',
      render: (_: string, record) => (
        <StatusCell
          {...record}
          status_id={record.status_id as unknown as number}
          statuses={statuses}
          onRefetch={refetch}
        />
      ),
      width: 150,
    },
    {
      title: t('table.columns.advertiser'),
      dataIndex: 'advertisers',
      key: 'advertisers',
      render: (_: string, record) => <AdvertiserCell {...record} />,
      width: 230,
    },
    {
      title: t('table.columns.countries'),
      dataIndex: 'countries',
      key: 'countries',
      render: (_: string, { countries }: NonCompliantAffiliate) => (
        <CountryList countries={countries} max={3} />
      ),
    },
    {
      title: t('table.columns.lastFound'),
      dataIndex: 'last_found',
      key: 'last_found',
      width: 150,
      sorter: true,
    },
    {
      key: 'actions',
      render: (_: string, record) => (
        <Space>
          <EditCell {...record} />
          <ActionsCell {...record} project={project} onRefetch={refetch} />
        </Space>
      ),
      width: 248,
      fixed: 'right',
    },
  ]

  return (
    <div className={clsx(styles.root)}>
      <Table
        emptyStub={
          {
            1: <TabNewStub project={project} />,
            2: <TabInProgressStub />,
            3: <TabResolvedStub />,
            4: <TabIgnoreStub />,
          }[status || 1]
        }
        toolbar={
          selectedRows.length ? (
            <div className={'flex items-center justify-between'}>
              <div className={'flex items-center gap-[24px]'}>
                <p className={'font-medium text-[#6684A7]'}>
                  {t('other.countSelected', { count: selectedRows.length })}
                </p>
                <div className={'flex items-center gap-[24px]'}>
                  <DropOptions
                    options={statuses
                      ?.filter((option) => Number(option.value) !== status)
                      .map((option) => ({
                        ...option,
                        text: t('fields.options.' + option.text),
                      }))}
                    verticalPosition={'top'}
                    onChange={(e) => {
                      updateStatus({
                        body: {
                          scenario: 'update_status',
                          ids: selectedRows.map((row) => row.id),
                          status_id: Number(e),
                        },
                      }).then(() => {
                        refetch()
                        setSelectedRows([])
                      })
                    }}
                  >
                    <button className={'flex items-center gap-[6px] text-[#A059FF]'}>
                      <div className={'w-[11px]'}>
                        <IconArrowRight color={'#A059FF'} />
                      </div>
                      {t('pages.PPCCompliance.moveTo')}
                    </button>
                  </DropOptions>
                  {selectedRows.find((row) => row.is_hidden) && (
                    <button
                      className={'flex items-center gap-[6px] text-[#A059FF]'}
                      onClick={() => {
                        hide({
                          body: { ids: selectedRows.map((row) => row.id), is_hidden: false },
                        }).then(() => {
                          refetch()
                          setSelectedRows([])
                        })
                      }}
                    >
                      <div className={'w-[13px]'}>
                        <IconEye color={'#A059FF'} />
                      </div>
                      {t('buttons.unhide')}
                    </button>
                  )}
                  {selectedRows.find((row) => !row.is_hidden) && (
                    <button
                      className={'flex items-center gap-[6px] text-[#A059FF]'}
                      onClick={() => {
                        hide({
                          body: { ids: selectedRows.map((row) => row.id), is_hidden: true },
                        }).then(() => {
                          refetch()
                          setSelectedRows([])
                        })
                      }}
                    >
                      <div className={'w-[13px]'}>
                        <IconEyeOff color={'#A059FF'} />
                      </div>
                      {t('buttons.hide')}
                    </button>
                  )}
                  <ExportDrop
                    columns={[
                      {
                        label: t('table.columns.affiliates'),
                        bold: true,
                        child: [
                          { keys: ['affiliate'], label: t('table.columns.affiliateId') },
                          { keys: ['ads_count'], label: t('table.columns.adsAnalyzed') },
                          {
                            keys: ['network_name'],
                            label: t('table.columns.network'),
                            tooltip: t('pages.PPCCompliance.networkTooltip'),
                          },
                          { keys: ['status_id'], label: t('table.columns.status') },
                          { keys: ['first_found'], label: t('table.columns.firstFound') },
                          { keys: ['last_found'], label: t('table.columns.lastFound') },
                        ],
                      },
                      {
                        keys: ['latest_examples'],
                        label: t('table.columns.latestExamples'),
                        tooltip: t('pages.PPCCompliance.latestExamplesTooltip'),
                        bold: true,
                      },
                    ]}
                    renderTrigger={({ open, onToggle, disabled }) => (
                      <button
                        aria-expanded={open}
                        className={'flex items-center gap-[6px] text-[#A059FF]'}
                        disabled={disabled}
                        type='button'
                        onClick={onToggle}
                      >
                        <div className={'w-[12px]'}>
                          <IconUpload color={'#A059FF'} />
                        </div>
                        {t('buttons.export')}
                      </button>
                    )}
                    dropPlacement={'top'}
                    loading={exportLoading}
                    onExport={(exportParams) =>
                      exportNonCompliantAffiliates({
                        ...queryParams,
                        ...exportParams,
                        projectId: Number(project),
                        affIds: selectedRows.map((row) => row.id),
                      }).then(() => setSelectedRows([]))
                    }
                  />
                </div>
              </div>
              <button className={'text-[#DC2F02]'} onClick={() => setSelectedRows([])}>
                {t('buttons.clearSelection')}
              </button>
            </div>
          ) : null
        }
        columns={columns}
        data={data}
        loading={isLoading || isFetching}
        rowClassName={(e) => (e.is_hidden ? 'disabled' : '')}
        selectedRowKeys={selectedRows}
        onPaginationChange={(page, pageSize, total) => {
          dispatch(
            setPpcCompliancePagination({
              current: page,
              pageSize: pageSize,
              total: total,
            }),
          )
          setQueryParams({
            ...queryParams,
            page: page,
            'per-page': pageSize,
          })
        }}
        onRowSelectionChange={(selection) => {
          setSelectedRows(selection)
        }}
        onSortingChange={(sorter) => {
          setQueryParams({
            ...queryParams,
            sort: sorter,
          })
        }}
      />
    </div>
  )
}
